import { Link } from "react-router-dom";
import { CheckCircle, ChevronRight, ListTodo } from "lucide-react";

const toneClasses = {
  rose: "bg-rose-50 border-rose-100 text-rose-950",
  amber: "bg-amber-50 border-amber-100 text-amber-950",
  blue: "bg-blue-50 border-blue-100 text-blue-950",
  emerald: "bg-emerald-50 border-emerald-100 text-emerald-950",
  slate: "bg-slate-50 border-slate-100 text-slate-900",
};

const iconToneClasses = {
  rose: "bg-white text-rose-700",
  amber: "bg-white text-amber-700",
  blue: "bg-white text-blue-700",
  emerald: "bg-white text-emerald-700",
  slate: "bg-white text-slate-700",
};

export default function ProfessionalTodayActions({ title, subtitle, actions, emptyLabel, testId }) {
  return (
    <section className="dash-card border-l-4 border-slate-800" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 rounded-xl" data-testid={`${testId}-icon`}>
            <ListTodo className="w-5 h-5 text-slate-700" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800" data-testid={`${testId}-title`}>{title}</h2>
            <p className="text-xs text-slate-500 mt-0.5" data-testid={`${testId}-subtitle`}>{subtitle}</p>
          </div>
        </div>
        <span className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full font-medium" data-testid={`${testId}-count`}>
          {actions.length}
        </span>
      </div>

      {actions.length === 0 ? (
        <div className="text-center py-6 text-slate-400" data-testid={`${testId}-empty`}>
          <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
          <p className="text-sm font-medium">{emptyLabel}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`${testId}-list`}>
          {actions.map(action => (
            <ProfessionalActionItem key={action.id} action={action} testId={`${testId}-item-${action.id}`} />
          ))}
        </div>
      )}
    </section>
  );
}

function ProfessionalActionItem({ action, testId }) {
  const Icon = action.icon;
  const className = `flex items-center justify-between gap-3 rounded-xl border p-3 text-left transition-[transform,background-color,border-color] duration-200 hover:border-slate-300 active:scale-[0.985] ${toneClasses[action.tone] || toneClasses.slate}`;
  const content = (
    <>
      <span className="flex min-w-0 items-center gap-3">
        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${iconToneClasses[action.tone] || iconToneClasses.slate}`} data-testid={`${testId}-icon-box`}>
          <Icon className="w-5 h-5" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-slate-800" data-testid={`${testId}-label`}>{action.label}</span>
          <span className="block text-xs text-slate-500 mt-0.5" data-testid={`${testId}-detail`}>{action.detail}</span>
        </span>
      </span>
      <span className="flex flex-shrink-0 items-center gap-2">
        {action.count !== undefined && (
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-slate-700 shadow-sm" data-testid={`${testId}-count`}>{action.count}</span>
        )}
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </span>
    </>
  );

  if (action.to) {
    return <Link to={action.to} className={className} data-testid={testId}>{content}</Link>;
  }

  return (
    <button type="button" onClick={action.onClick} className={className} data-testid={testId}>
      {content}
    </button>
  );
}